/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { globalRef, noop, parseBoolean } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { queryParam } from '@/util/queryParams'

const { errorHandler } = ctx('startFullscreen')

const requestState = globalRef(() => ({ requested: false }))

/**
 * Honors `?fullscreen=1`: requests full screen once per page load. A browser that refuses
 * without user activation gets a confirm modal whose button re-requests inside the click.
 */
export function startFullscreenFromQuery(): void {
    const raw = queryParam('fullscreen')
    if (requestState.value.requested || raw == null || !parseBoolean(raw)) return
    requestState.value.requested = true
    const root = document.documentElement
    if (typeof root.requestFullscreen !== 'function') return
    root.requestFullscreen().catch(() => promptForFullscreen(root))
}

function promptForFullscreen(root: HTMLElement): void {
    Promise.all([import('@/components/systemModal'), import('@/fullscreen/i18n/bundle.generated')])
        .then(([{ openSystemModal }, { getString }]) =>
            openSystemModal({
                title: getString('dmusic_amp_fullscreen_prompt_title'),
                body: getString('dmusic_amp_fullscreen_prompt_exit_hint'),
                primary: {
                    label: getString('dmusic_amp_fullscreen_prompt_open'),
                    run: () => {
                        root.requestFullscreen().catch(noop)
                    },
                },
                secondary: {
                    label: getString('dmusic_amp_fullscreen_prompt_dismiss'),
                    run: noop,
                },
            })
        )
        .catch(errorHandler)
}
